import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Briefcase } from 'lucide-react';
import { ContractsV2Shell } from './DemoShell';
import { useContractsV2, selectFundCoverage } from './ContractsV2Context';
import type { ContractV2Bundle } from './mockTypes';

const formatRmb = (n: number) =>
  n >= 1_000_000 ? `¥${(n / 1_000_000).toFixed(1)}m`
  : n >= 1_000 ? `¥${(n / 1_000).toFixed(1)}k`
  : `¥${n.toFixed(0)}`;

const stateLabel: Record<string, { text: string; cls: string }> = {
  draft: { text: '草稿', cls: 'bg-slate-100 text-slate-600' },
  pending_other_side: { text: '等待对方确认', cls: 'bg-amber-100 text-amber-700' },
  rejected: { text: '已拒绝', cls: 'bg-rose-100 text-rose-700' },
  active: { text: '进行中', cls: 'bg-emerald-100 text-emerald-700' },
  completed: { text: '已完成', cls: 'bg-blue-100 text-blue-700' },
  cancelled: { text: '已取消', cls: 'bg-slate-100 text-slate-500' },
  disputed: { text: '争议中', cls: 'bg-rose-100 text-rose-700' },
};

const ContractRow: React.FC<{ bundle: ContractV2Bundle }> = ({ bundle }) => {
  const { isViewerClient } = useContractsV2();
  const { contract, milestones } = bundle;
  const cov = selectFundCoverage(bundle);
  const stateChip = stateLabel[contract.acceptanceState] || stateLabel.draft;
  const completedCount = milestones.filter(m => m.status === 'COMPLETED').length;
  const otherParty = isViewerClient(bundle) ? contract.creator : contract.client;
  const myRole = isViewerClient(bundle) ? '客户' : '创作者';

  return (
    <Link
      to={`/contracts-v2/${contract.id}`}
      className="block bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all p-5"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
              contract.projectType === 'large' ? 'bg-purple-50 text-purple-700' : 'bg-sky-50 text-sky-700'
            }`}>
              {contract.projectType === 'large' ? '大项目' : '小项目'}
            </span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${stateChip.cls}`}>
              {stateChip.text}
            </span>
          </div>
          <h3 className="text-base font-semibold text-slate-900 truncate">{contract.title}</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            我作为 <span className="font-medium text-slate-700">{myRole}</span> · 对方：
            <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">
              {otherParty.nickname}
            </span>
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-semibold text-slate-900">{formatRmb(contract.totalAmount)}</div>
          <div className="text-[11px] text-slate-400">总金额</div>
        </div>
      </div>

      {contract.projectType === 'large' && (
        <div className="grid grid-cols-4 gap-3 pt-3 border-t border-slate-100 text-xs">
          <Stat label="里程碑" value={`${completedCount}/${milestones.length}`} />
          <Stat label="已付款" value={formatRmb(cov.totalPaid)} />
          <Stat label="已放款" value={formatRmb(cov.totalReleased)} />
          <Stat label="资金余额" value={formatRmb(cov.availableBalance)} positive={cov.availableBalance > 0} />
        </div>
      )}
    </Link>
  );
};

const Stat: React.FC<{ label: string; value: string; positive?: boolean }> = ({ label, value, positive }) => (
  <div>
    <div className={`font-semibold ${positive ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
  </div>
);

const ContractsListPage: React.FC = () => {
  const { bundles, viewerId } = useContractsV2();
  // Show only contracts where current viewer is a party.
  const visible = bundles.filter(b =>
    b.contract.client.userId === viewerId || b.contract.creator.userId === viewerId
  );

  return (
    <ContractsV2Shell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">我的合同</h1>
          <p className="text-sm text-slate-500 mt-1">
            演示数据 · 切换上方"视角"按钮可切换客户/创作者两种身份。
          </p>
        </div>
        <Link
          to="/contracts-v2/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
        >
          <Plus className="w-4 h-4" /> 发起新合同
        </Link>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">暂无合同</p>
          <Link
            to="/contracts-v2/new"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> 发起首个合同
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(b => <ContractRow key={b.contract.id} bundle={b} />)}
        </div>
      )}

      <div className="mt-10 text-xs text-slate-400 flex items-center gap-2">
        <Briefcase className="w-3 h-3" />
        Demo · 所有数据保存在浏览器内存中，刷新页面会重置。
      </div>
    </ContractsV2Shell>
  );
};

export default ContractsListPage;
